import { useState } from 'react';
import { cn } from '@/shared/lib/utils';
import type { ErrorToast as ErrorToastType } from '@/shared/stores/errorToastStore';

interface ErrorToastProps {
  toast: ErrorToastType;
  onClose: () => void;
}

export function ErrorToast({ toast, onClose }: ErrorToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = () => {
    setIsExiting(true);
    // Wait for exit animation before removing from DOM
    setTimeout(onClose, 150);
  };

  // Get severity color based on status code
  const isClientError = toast.statusCode >= 400 && toast.statusCode < 500;
  const isServerError = toast.statusCode >= 500;
  const isNetworkError = toast.statusCode > 0; // 0 means client-side error

  return (
    <div
      className={cn(
        'relative w-full rounded-xl overflow-hidden',
        'bg-gradient-to-br from-red-50 to-white',
        'border border-red-200/50',
        'transition-all duration-300 ease-out',
        isExiting
          ? 'opacity-0 translate-x-full scale-95'
          : 'opacity-100 translate-x-0 scale-100 animate-slide-in-right'
      )}
      style={{
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Gradient accent bar on left */}
      <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-red-500 to-red-700" />

      {/* Content */}
      <div className="flex items-start gap-3 p-4 pl-5">
        {/* Icon */}
        <div className="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-red-500 to-red-700 shadow-sm shadow-red-500/30">
          <svg
            className="h-5 w-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0 pt-0.5">
          {/* Status Code Badge - only show for network errors */}
          {isNetworkError && (
            <div className="inline-flex items-center gap-1.5 mb-1.5">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-xs font-semibold font-mono tracking-wide">
                {toast.statusCode}
              </span>
              <span className="text-xs font-medium text-red-600">
                {isServerError
                  ? 'Server Error'
                  : isClientError
                  ? 'Request Failed'
                  : 'Error'}
              </span>
            </div>
          )}

          {/* Error Message */}
          <p className="text-sm text-gray-800 leading-snug line-clamp-2 font-roboto">
            {toast.message}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className={cn(
            'flex-shrink-0 flex h-7 w-7 items-center justify-center rounded-lg',
            'text-gray-400 hover:text-gray-600',
            'hover:bg-emerald-100/50',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-emerald-500/20',
            'group'
          )}
          aria-label="Close notification"
        >
          <svg
            className="h-4 w-4 transition-transform duration-200 group-hover:scale-110"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
